import { useEffect } from "react";

import { useAuth } from "react-oidc-context";
import { useLocation, useNavigate } from "react-router-dom";

import AuthenticatedRoutes from "./AuthenticatedRoutes";
import ScrollRestoration from "./ScrollRestoration";
import UnauthenticatedRoutes from "./UnauthenticatedRoutes";
import { useServiceInitialization } from "../hooks/useServiceInitialization";
import { RootState } from "../store";
import { useAppSelector } from "../store/hooks";

// Paths that should not trigger automatic signin redirect
const SIGNOUT_PATHS = ["/signout-callback", "/logout", "/logged-out", "/appinsight"];

// Key for storing serviceId in sessionStorage for cross-redirect persistence
const SERVICE_ID_STORAGE_KEY = "pending_service_id";

function Routes() {
  const location = useLocation();
  const navigate = useNavigate();
  const auth = useAppSelector((state: RootState) => state.auth);
  const { isAuthenticated, user } = auth;

  // Use the OIDC auth context
  const oidcAuth = useAuth();

  // Initialize service selection
  useServiceInitialization();

  // Check if current path is a signout-related path or appinsight test page
  const isExcludedPath = SIGNOUT_PATHS.some((path) => location.pathname.toLowerCase().startsWith(path.toLowerCase()));
  // Capture serviceId from URL and store in sessionStorage, then redirect to /services for processing
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const serviceId = searchParams.get("serviceId");
    if (serviceId) {
      sessionStorage.setItem(SERVICE_ID_STORAGE_KEY, serviceId);
      
      // If authenticated and not already on /services, redirect to /services to process the serviceId
      const isAuthenticated = oidcAuth.isAuthenticated || (auth.isAuthenticated && auth.user);
      if (isAuthenticated && location.pathname !== "/services") {
        navigate("/services", { replace: true });
      }
    }
  }, [location.search, location.pathname, oidcAuth.isAuthenticated, auth.isAuthenticated, auth.user, navigate]);

  // Check if current path is a signout-related path
  const isSignoutPath = SIGNOUT_PATHS.some((path) => location.pathname.toLowerCase().startsWith(path.toLowerCase()));

  // Automatically redirect to identity server when not authenticated
  useEffect(() => {
    // Don't redirect for excluded paths (signout, appinsight test)
    if (isExcludedPath) {
      return;
    }

    if (!oidcAuth.isAuthenticated && !oidcAuth.isLoading && !oidcAuth.error) {
      // Store the current location to return to after authentication using localStorage
      localStorage.setItem("original_route", location.pathname + location.search);
      oidcAuth.signinRedirect();
    }
  }, [oidcAuth.isAuthenticated, oidcAuth.isLoading, oidcAuth.error, location, oidcAuth, isExcludedPath]);

  // For excluded paths, always show UnauthenticatedRoutes (bypassing auth check)
  if (isExcludedPath) {
    return (
      <>
        <ScrollRestoration />
        <UnauthenticatedRoutes />
      </>
    );
  }

  // Show loading while OIDC is initializing
  if (oidcAuth.isLoading) {
    return <div>Loading...</div>;
  }

  // Show error if OIDC failed
  if (oidcAuth.error) {
    return <div>Authentication error: {oidcAuth.error.message}</div>;
  }

  return (
    <>
      <ScrollRestoration />
      {(isAuthenticated && user) || oidcAuth.isAuthenticated ? <AuthenticatedRoutes /> : <UnauthenticatedRoutes />}
    </>
  );
}

export default Routes;
